import type { ReactNode } from 'react';

export type RecordMetaRow = { label: string; value: ReactNode };

export function RecordMetaCard({
  title,
  rows,
  footer,
  loading,
  emptyMessage,
}: {
  title?: string;
  rows: RecordMetaRow[];
  footer?: ReactNode;
  loading?: boolean;
  emptyMessage?: string;
}) {
  if (loading) {
    return (
      <div aria-busy="true" aria-label="Loading details">
        {title ? <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary">{title}</p> : null}
        <div>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="border-b border-[var(--vscode-border-primary)] py-2.5 last:border-b-0">
              <div className="mb-1 h-3 w-16 animate-pulse rounded bg-[var(--vscode-bg-tertiary)]" />
              <div className="h-4 w-32 animate-pulse rounded bg-[var(--vscode-bg-tertiary)]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div>
        {title ? <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary">{title}</p> : null}
        <p className="text-[13px] vscode-text-tertiary">{emptyMessage ?? 'No metadata.'}</p>
        {footer ? <div className="mt-4 border-t border-[var(--vscode-border-primary)] pt-3">{footer}</div> : null}
      </div>
    );
  }

  return (
    <div>
      {title ? <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary">{title}</p> : null}
      <dl>
        {rows.map((row) => (
          <div
            key={row.label}
            className="border-b border-[var(--vscode-border-primary)] py-2.5 last:border-b-0"
          >
            <dt className="mb-0.5 text-[11px] font-medium uppercase tracking-[0.05em] text-zinc-500">{row.label}</dt>
            <dd className="min-w-0 text-[13px] text-zinc-900 dark:text-zinc-100 vscode-text-primary">{row.value}</dd>
          </div>
        ))}
      </dl>
      {footer ? <div className="mt-4 border-t border-[var(--vscode-border-primary)] pt-3">{footer}</div> : null}
    </div>
  );
}
